import { posthogHeaders } from './analytics/client';
import type { OwnerPayload, VisitorPayload } from '../core/payload';

export type GetToken = () => Promise<string | null>;
export class ApiError extends Error {
  constructor(public status: number, public reason: string, message: string, public body: Record<string, unknown> = {}) { super(message); }
}

/** One request to the Worker with the Clerk token; any failure arrives as an `ApiError`. */
export async function call<T>(getToken: GetToken, path: string, options: RequestInit = {}): Promise<T> {
  try {
    const token = await getToken();
    const response = await fetch(path, { ...options, signal: options.signal ?? AbortSignal.timeout(15_000), headers: { ...posthogHeaders(), ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers } });
    const body = (response.status === 204 ? {} : await response.json()) as Record<string, unknown> & { error?: string; reason?: string; message?: string };
    if (!response.ok) throw new ApiError(response.status, body.reason ?? body.error ?? 'unavailable', body.message ?? 'We could not finish that request.', body);
    return body as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'unreachable', 'We could not connect. Check your connection and try again.');
  }
}

/** `GET /api/me`: who the owner gate admitted, and whether verification is switched on. */
export interface Me { userId: string; email: string | null; staff: boolean; verify: boolean; fixtures?: boolean }

/** The owner's own payload (`ownerPayload` in `core/payload.ts`). */
export type OwnerProfile = OwnerPayload;
export type OwnerCertification = OwnerPayload['certifications'][number];
export type OwnerJob = OwnerPayload['jobs'][number];
export type OwnerAward = OwnerPayload['awards'][number];

/** `GET /api/p/:slug`. `owner` is set only when the owner opens their own link. */
export type VisitorProfile = VisitorPayload & { owner?: boolean };
